import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Modal, Field, Select, Spinner } from '../components/common/UI';
import toast from 'react-hot-toast';
import { errMsg } from '../utils/helpers';

export function NewOrderModal({ onClose }) {
  const { isProducer, isDealerAdmin, hasPerm } = useAuth();
  const canManageSettlementParties = (isProducer || isDealerAdmin) && hasPerm('orders', 'view_prices');
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', description: '', dealer_id: '', payer_company_id: '', receiver_company_id: '', deadline: '' });
  const [dealers, setDealers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      isProducer ? api.get('/admin/dealers') : Promise.resolve({ data: [] }),
      api.get('/admin/companies')
    ]).then(([d, c]) => {
      setDealers(d.data.map(x => ({ value: x.id, label: x.name })));
      setCompanies(c.data);
    });
  }, [isProducer]);

  const payerOpts = companies
    .filter(c => !form.dealer_id || c.dealer_id === +form.dealer_id)
    .map(c => ({ value: c.id, label: c.name + ((isProducer || isDealerAdmin) && c.dealer_pct > 0 ? ' (' + c.dealer_pct + '%)' : '') }));
  const receiverOpts = companies.filter(c => !c.dealer_id).map(c => ({ value: c.id, label: c.name }));

  const save = async () => {
    if (!form.title) { toast.error('Укажите название заказа'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/orders', form);
      toast.success('Заказ создан');
      onClose();
      navigate('/orders/' + data.id);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Новый заказ" onClose={onClose} size="lg">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Field label="Название заказа" required>
            <input className="input" value={form.title} autoFocus
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Кратко опишите заказ" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Описание / ТЗ">
            <textarea className="input resize-none" rows={4} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </Field>
        </div>
        {isProducer && (
          <Field label="Дилер" required>
            <Select value={form.dealer_id}
              onChange={v => setForm(f => ({ ...f, dealer_id: v, payer_company_id: '' }))}
              options={dealers} placeholder="Выберите дилера" />
          </Field>
        )}
        {canManageSettlementParties && (<>
          <Field label="Плательщик (дилер)">
            <Select value={form.payer_company_id}
              onChange={v => setForm(f => ({ ...f, payer_company_id: v }))}
              options={payerOpts} placeholder="Компания дилера" />
          </Field>
          <Field label="Получатель (моя компания)">
            <Select value={form.receiver_company_id}
              onChange={v => setForm(f => ({ ...f, receiver_company_id: v }))}
              options={receiverOpts} placeholder="Моя компания" />
          </Field>
        </>)}
        <Field label="Дедлайн">
          <input type="date" className="input" value={form.deadline}
            onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
        </Field>
      </div>
      <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-800">
        <button className="btn-secondary" onClick={onClose}>Отмена</button>
        <button className="btn-primary" onClick={save} disabled={saving}>
          {saving ? <Spinner size={14} /> : 'Создать заказ'}
        </button>
      </div>
    </Modal>
  );
}

export function NewRepairModal({ onClose }) {
  const { isProducer } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    dealer_id: '',
    device_type: 'external',
    product_id: '',
    item_name: '',
    serial_number: '',
    symptoms: '',
    is_warranty: false,
    end_user_info: '',
    received_at: new Date().toISOString().slice(0, 10),
    sale_date: '',
  });
  const [dealers, setDealers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [kitLines, setKitLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      isProducer ? api.get('/admin/dealers') : Promise.resolve({ data: [] }),
      api.get('/catalog/products', { params: { active_only: 'true' } }),
      api.get('/catalog/categories'),
    ]).then(([d, p, c]) => {
      setDealers(d.data.map(x => ({ value: x.id, label: x.name })));
      setProducts(p.data);
      setCategories(c.data);
    });
  }, [isProducer]);

  const catOpts = categories.map(c => ({ value: c.id, label: c.name }));
  const filteredProducts = selectedCat ? products.filter(p => p.category_id === +selectedCat) : products;
  const productOpts = filteredProducts.map(p => ({ value: p.id, label: p.name + (p.sku ? ' [' + p.sku + ']' : '') }));

  const selectProduct = (id) => {
    const prod = products.find(p => p.id === +id);
    setForm(f => ({ ...f, product_id: id, item_name: prod?.name || '' }));
  };

  const addKitLine = () => {
    setKitLines(ls => [...ls, { _tempId: Date.now() + Math.random(), category_id: '', product_id: '', product_name: '', quantity: 1 }]);
  };

  const updateKitLine = (idx, patch) => {
    setKitLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const selectKitLineCategory = (idx, categoryId) => {
    updateKitLine(idx, { category_id: categoryId, product_id: '', product_name: '' });
  };

  const selectKitLineProduct = (idx, pid) => {
    const prod = products.find(p => p.id === +pid);
    updateKitLine(idx, {
      product_id: pid,
      product_name: prod?.name || '',
      category_id: prod?.category_id || kitLines[idx]?.category_id || '',
    });
  };

  const validate = () => {
    if (isProducer && !form.dealer_id) {
      toast.error('Выберите дилера');
      return false;
    }
    if (form.device_type === 'catalog_product') {
      if (!form.product_id) {
        toast.error('Выберите типовое устройство');
        return false;
      }
    } else if (form.device_type === 'catalog_kit') {
      if (!form.item_name.trim()) {
        toast.error('Укажите название комплекта');
        return false;
      }
      if (!kitLines.length || kitLines.some(l => !l.product_id)) {
        toast.error('Комплект должен содержать товары из справочника');
        return false;
      }
    } else if (!form.item_name.trim()) {
      toast.error('Укажите название оборудования');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        product_id: form.device_type === 'catalog_product' ? form.product_id : '',
        kit_lines: form.device_type === 'catalog_kit'
          ? kitLines.map((l, i) => ({ product_id: l.product_id, quantity: l.quantity || 1, sort_order: i }))
          : undefined,
      };
      const { data } = await api.post('/repairs', payload);
      toast.success('Запись о ремонте создана');
      onClose();
      navigate('/repairs/' + data.id);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Новый ремонт" onClose={onClose} size="xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isProducer && (
          <Field label="Дилер" required>
            <Select value={form.dealer_id}
              onChange={v => setForm(f => ({ ...f, dealer_id: v }))}
              options={dealers} placeholder="Выберите дилера" />
          </Field>
        )}
        <Field label="Дата поступления">
          <input type="date" className="input" value={form.received_at}
            onChange={e => setForm(f => ({ ...f, received_at: e.target.value }))} />
        </Field>
        <Field label="Дата продажи" hint="Для оценки срока эксплуатации">
          <input type="date" className="input" value={form.sale_date}
            onChange={e => setForm(f => ({ ...f, sale_date: e.target.value }))} />
        </Field>

        <div className="md:col-span-2">
          <Field label="Тип единицы" required>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { value: 'catalog_product', label: 'Типовое устройство', icon: '📦' },
                { value: 'catalog_kit', label: 'Новый комплект', icon: '🗂' },
                { value: 'external', label: 'Чужое оборудование', icon: '🔧' },
              ].map(opt => (
                <label key={opt.value} className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors border-gray-700 hover:border-gray-500"
                  style={form.device_type === opt.value ? { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.1)' } : {}}>
                  <input type="radio" name="new_repair_device_type" value={opt.value}
                    checked={form.device_type === opt.value}
                    onChange={e => {
                      const nextType = e.target.value;
                      setForm(f => ({ ...f, device_type: nextType, product_id: '', item_name: '' }));
                      if (nextType !== 'catalog_kit') setKitLines([]);
                    }}
                    className="accent-blue-500" />
                  <span className="text-sm">{opt.icon} {opt.label}</span>
                </label>
              ))}
            </div>
          </Field>
        </div>

        {form.device_type === 'catalog_product' && (
          <>
            <Field label="Категория">
              <Select value={selectedCat}
                onChange={v => { setSelectedCat(v); setForm(f => ({ ...f, product_id: '', item_name: '' })); }}
                options={catOpts} placeholder="Все категории" />
            </Field>
            <Field label="Типовое устройство" required>
              <Select value={form.product_id}
                onChange={selectProduct}
                options={productOpts} placeholder="Выберите товар" />
            </Field>
          </>
        )}

        <div className="md:col-span-2">
          <Field label={form.device_type === 'catalog_kit' ? 'Название комплекта' : form.device_type === 'external' ? 'Название оборудования' : 'Наименование'} required>
            <input className="input" value={form.item_name} autoFocus
              onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))}
              placeholder={form.device_type === 'catalog_kit'
                ? 'Например: Комплект автоматики ворот'
                : form.device_type === 'external'
                  ? 'Марка, модель оборудования'
                  : 'Заполняется по выбранному товару'} />
          </Field>
        </div>

        {form.device_type === 'catalog_kit' && (
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <span className="label">Состав комплекта</span>
              <button className="btn-secondary text-xs" onClick={addKitLine}>Добавить позицию</button>
            </div>
            <p className="text-xs text-gray-500 mb-2">Новый комплект собирается здесь из любых товаров справочника. Категории можно смешивать.</p>
            {kitLines.length === 0 ? (
              <p className="text-xs text-gray-600 text-center py-4 border border-dashed border-gray-700 rounded-lg">Добавьте товары из справочника. Категории можно смешивать, количество задаётся на комплект.</p>
            ) : (
              <div className="space-y-2">
                {kitLines.map((line, idx) => {
                  const lineProducts = line.category_id ? products.filter(p => p.category_id === +line.category_id) : products;
                  const lineOptions = lineProducts.map(p => ({ value: p.id, label: p.name + (p.sku ? ' [' + p.sku + ']' : '') }));
                  return (
                    <div key={line.id || line._tempId || idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_96px_36px] gap-2 items-center">
                      <Select value={line.category_id}
                        onChange={v => selectKitLineCategory(idx, v)}
                        options={catOpts} placeholder="Категория" />
                      <Select value={line.product_id}
                        onChange={v => selectKitLineProduct(idx, v)}
                        options={lineOptions} placeholder="Устройство в комплекте" />
                      <input type="number" min="1" step="1" className="input text-center"
                        value={line.quantity}
                        onChange={e => updateKitLine(idx, { quantity: e.target.value })} />
                      <button className="btn-ghost p-1 hover:text-red-400"
                        onClick={() => setKitLines(ls => ls.filter((_, i) => i !== idx))}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <Field label="Серийный номер">
          <input className="input mono" value={form.serial_number}
            onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
        </Field>
        <div className="flex items-center pt-5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="accent-green-500 w-4 h-4"
              checked={form.is_warranty}
              onChange={e => setForm(f => ({ ...f, is_warranty: e.target.checked }))} />
            <span className="text-sm text-gray-300">Гарантийный ремонт</span>
          </label>
        </div>
        <div className="md:col-span-2">
          <Field label="Симптомы / описание неисправности">
            <textarea className="input resize-none" rows={3} value={form.symptoms}
              onChange={e => setForm(f => ({ ...f, symptoms: e.target.value }))} />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Пользователь оборудования / контакт для связи">
            <textarea className="input resize-none" rows={4} value={form.end_user_info}
              onChange={e => setForm(f => ({ ...f, end_user_info: e.target.value }))}
              placeholder="Название компании, ФИО, телефон, email" />
          </Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-800">
        <button className="btn-secondary" onClick={onClose}>Отмена</button>
        <button className="btn-primary" onClick={save} disabled={saving}>
          {saving ? <Spinner size={14} /> : 'Создать'}
        </button>
      </div>
    </Modal>
  );
}


export function NewSupplyModal({ onClose }) {
  const { isProducer } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    dealer_id: '', description: '', supplier_info: '',
    payer_company_id: '', receiver_company_id: '', eta_to_warehouse: ''
  });
  const [dealers, setDealers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      isProducer ? api.get('/admin/dealers') : Promise.resolve({ data: [] }),
      api.get('/admin/companies')
    ]).then(([d, c]) => {
      setDealers(d.data.map(x => ({ value: x.id, label: x.name })));
      setCompanies(c.data);
    });
  }, [isProducer]);

  const payerOpts = companies
    .filter(c => !form.dealer_id || c.dealer_id === +form.dealer_id)
    .map(c => ({ value: c.id, label: c.name }));
  const receiverOpts = companies.filter(c => !c.dealer_id).map(c => ({ value: c.id, label: c.name }));

  const save = async () => {
    if (!form.description) { toast.error('Укажите описание поставки'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/supplies', form);
      toast.success('Поставка создана');
      onClose();
      navigate('/supplies/' + data.id);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Новая поставка" onClose={onClose} size="lg">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isProducer && (
          <div className="md:col-span-2">
            <Field label="Дилер" required>
              <Select value={form.dealer_id}
                onChange={v => setForm(f => ({ ...f, dealer_id: v, payer_company_id: '' }))}
                options={dealers} placeholder="Выберите дилера" />
            </Field>
          </div>
        )}
        <div className="md:col-span-2">
          <Field label="Описание поставки" required>
            <textarea className="input resize-none" rows={2} value={form.description} autoFocus
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Что заказываем (кратко)" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Поставщик" hint="Название, контакты — заполняется по желанию">
            <textarea className="input resize-none" rows={2} value={form.supplier_info}
              onChange={e => setForm(f => ({ ...f, supplier_info: e.target.value }))}
              placeholder="Название компании, контактное лицо..." />
          </Field>
        </div>
        <Field label="Плательщик (дилер)">
          <Select value={form.payer_company_id}
            onChange={v => setForm(f => ({ ...f, payer_company_id: v }))}
            options={payerOpts} placeholder="Компания" />
        </Field>
        <Field label="Получатель (моя компания)">
          <Select value={form.receiver_company_id}
            onChange={v => setForm(f => ({ ...f, receiver_company_id: v }))}
            options={receiverOpts} placeholder="Компания" />
        </Field>
        <Field label="Прогноз поступления на склад">
          <input type="date" className="input" value={form.eta_to_warehouse}
            onChange={e => setForm(f => ({ ...f, eta_to_warehouse: e.target.value }))} />
        </Field>
      </div>
      <p className="text-xs text-gray-600 mt-3">Позиции поставки добавляются в карточке после создания</p>
      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-800">
        <button className="btn-secondary" onClick={onClose}>Отмена</button>
        <button className="btn-primary" onClick={save} disabled={saving}>
          {saving ? <Spinner size={14} /> : 'Создать поставку'}
        </button>
      </div>
    </Modal>
  );
}
